import React, { useState } from "react";
import { Modal, Button, Tabs, Tab } from "react-bootstrap";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Bar,
  BarChart,
  Radar,
  ReferenceDot
} from "recharts";
import CalendarHeatmap from "react-calendar-heatmap";
import "react-calendar-heatmap/dist/styles.css";
import "./Insights.css";
const PIE_COLORS = ["#34d399", "#f87171", "#bdbdbd"];

// Utility: robust date range for calendar
function getCalendarDomain(data) {
  if (!data || data.length === 0) {
    const now = new Date();
    return {
      start: new Date(now.getFullYear(), now.getMonth() - 2, 1),
      end: new Date()
    };
  }
  const dates = data.map(d => new Date(d.date));
  const minDate = new Date(Math.min(...dates));
  const maxDate = new Date(Math.max(...dates));
  return { start: minDate, end: maxDate };
}
// Stopwords for word freq
const stopwords = new Set([
  "the","a","an","and","to","of","in","on","at","is","for","with","as","by","it","i","was","that","this","my","you","me","but","so","from","if","or","be","are","were","we","he","she","they","them","our","about","out","into","over","just"
]);
function topWords(words, n = 10) {
  const counts = {};
  words.forEach(w => {
    if (!stopwords.has(w) && w.length > 2) counts[w] = (counts[w] || 0) + 1;
  });
  return Object.entries(counts)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, n);
}
function tokenizeAllText(entries, columns) {
  const words = [];
  for (const row of entries) {
    for (const col of columns.filter(c => c.type === "text")) {
      let val = (row[col.key] || "").toLowerCase();
      val.replace(/[.,!?]/g, "")
        .split(/\s+/)
        .forEach(w => {
          if (w) words.push(w);
        });
    }
  }
  return words;
}
function wordFreqRadarData(words, n = 10) {
  const list = topWords(words, n);
  while (list.length < n) list.push({ word: "-", count: 0 });
  return list;
}
// --- BLUE GITHUB-LIKE CALENDAR HEATMAP STYLING ---
const customCalendarTheme = {
  colorEmpty: "#e0e7ef",      // light gray-blue for empty days
  colorScale1: "#dbeafe",     // very light blue
  colorScale2: "#60a5fa",     // medium blue
  colorScale3: "#2563eb",     // bold blue
  colorScale4: "#082f49"      // dark blue
};
function getHeatmapClass(val) {
  if (!val || !val.count) return "bl-empty";
  if (val.count < 3) return "bl-1";
  if (val.count < 7) return "bl-2";
  if (val.count < 15) return "bl-3";
  return "bl-4";
}
function formatNumber(n, digits = 2) {
  if (n == null) return "-";
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: digits });
}
function percent(val, total) {
  if (!total) return "0%";
  return ((val / total) * 100).toFixed(0) + "%";
}
function groupCalendarByYear(calendar_heatmap) {
  // returns { [year]: [entries] }
  const out = {};
  (calendar_heatmap || []).forEach(item => {
    if (!item.year && item.date) item.year = new Date(item.date).getFullYear();
    if (!out[item.year]) out[item.year] = [];
    out[item.year].push(item);
  });
  return out;
}

function getYearDomain(entries) {
  // Returns {start, end} for the year (Sunday on week 1 to Saturday on last week)
  if (!entries || !entries.length) return { start: undefined, end: undefined };
  const year = new Date(entries[0].date).getFullYear();
  const jan1 = new Date(year, 0, 1);
  // Find first Sunday
  const firstSunday = new Date(jan1);
  firstSunday.setDate(jan1.getDate() - jan1.getDay());
  // Find last date in data (or Dec 31)
  const dec31 = new Date(year, 11, 31);
  // Find last Saturday after Dec 31
  const lastSaturday = new Date(dec31);
  lastSaturday.setDate(dec31.getDate() + (6 - dec31.getDay()));
  return { start: firstSunday, end: lastSaturday };
}

function InsightsTrends({ show, onHide, insights, logEntries, columns, objective, historyMode }) {
  const [wordTab, setWordTab] = useState("bar");
  const [activeIndex, setActiveIndex] = useState(null);

  if (!show || !insights) return null;
  const {
    ai_summary,
    moving_avg_series = [],
    pie_data = [],
    sentiment_band_timeline = [],
    calendar_heatmap = [],
    intensity_iqr = [0, 0],
    intensity_mean = 0,
    word_freq = [],
    analyzed_at
  } = insights;
  const words = tokenizeAllText(logEntries || [], columns || []);
  const radarData = wordFreqRadarData(words, 10);
  const pieSum = pie_data.reduce((a, b) => a + (b.value || 0), 0);

  // --- CALENDAR MULTI-YEAR ---
  const calendarByYear = groupCalendarByYear(calendar_heatmap);
  const years = Object.keys(calendarByYear).sort();

  // --- Behavioral Bands chart prep ---
  const bandData = (sentiment_band_timeline || []).map(d => ({
    ...d,
    event_compound: typeof d.event_compound === "number" ? d.event_compound : 0,
    feeling_compound: typeof d.feeling_compound === "number" ? d.feeling_compound : 0,
    delta: typeof d.delta === "number" ? d.delta : 0,
    date: d.date,
    band: d.band,
    band_desc: d.band_desc,
    event_sentiment: d.event_sentiment,
    feeling_sentiment: d.feeling_sentiment
  }));
  const chartColors = {
    event: "#60a5fa",
    feeling: "#fb7185",
    delta: "#10b981"
  };
  const showBand = (activeIndex != null && bandData[activeIndex]);

  return (
    <Modal show={show} onHide={onHide} centered size="lg" dialogClassName="vybly-insights-modal">
      <Modal.Header closeButton>
        <Modal.Title>Logbook Insights & Trends</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* AI summary */}
        {ai_summary && (
          <div className="ai-summary-box">
            <b>Insights:</b>
            <div style={{ whiteSpace: "pre-line" }}>{ai_summary}</div>
          </div>
        )}
        {/* 1. Moving window average: pos/neg */}
        <div className="insights-section">
          <h5>Emotional Trends: Positive & Negative (5-entry Moving Avg)</h5>
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={moving_avg_series}>
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="pos_avg" stroke="#34d399" strokeWidth={2} name="Positive" dot={false} />
              <Line type="monotone" dataKey="neg_avg" stroke="#f87171" strokeWidth={2} name="Negative" dot={false} />
            </LineChart>
          </ResponsiveContainer>
          <div className="insights-caption">
            Rolling averages reveal real momentum—showing improvement, challenges, or recurring cycles without being distorted by outliers.
          </div>
        </div>
        {/* 2. Sentiment: pie (last 30d) */}
        <div className="insights-section">
          <h5>Sentiment Breakdown (Last 30 Days)</h5>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={pie_data}
                cx="50%"
                cy="50%"
                outerRadius={60}
                fill="#8884d8"
                dataKey="value"
                label={entry =>
                  `${percent(entry.value, pieSum)}`
                }
              >
                {pie_data.map((entry, idx) => (
                  <Cell key={`cell-${idx}`} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="insights-caption">
            Pie chart shows your positive, negative, and neutral entries as a percentage of all entries in the last 30 days.
          </div>
        </div>
        {/* 3. Behavioral Bands: Event vs. Feeling Sentiment Over Time */}
        <div className="insights-section">
          <h5>
            Behavioral Bands: Event vs. Feeling Sentiment Over Time
            <span
              style={{
                fontSize: 15,
                fontWeight: 400,
                marginLeft: 7,
                color: "#555"
              }}
            >
              (swipe/scroll to explore)
            </span>
          </h5>
          <ResponsiveContainer width="100%" height={230}>
            <LineChart
              data={bandData}
              margin={{ top: 16, right: 40, left: 16, bottom: 8 }}
              onMouseMove={state => {
                if (state && state.activeTooltipIndex !== undefined) {
                  setActiveIndex(state.activeTooltipIndex);
                }
              }}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <XAxis dataKey="date" />
              <YAxis domain={[-1.05, 1.05]} ticks={[-1, -0.5, 0, 0.5, 1]} />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload || !payload.length) return null;
                  const d = payload[0].payload;
                  return (
                    <div className="custom-band-tooltip">
                      <div>
                        <b>{d.date}</b>
                        <br />
                        <span style={{ color: chartColors.event }}>Event: {d.event_sentiment} ({d.event_compound.toFixed(2)})</span>
                        <br />
                        <span style={{ color: chartColors.feeling }}>Feeling: {d.feeling_sentiment} ({d.feeling_compound.toFixed(2)})</span>
                        <br />
                        <span style={{ color: chartColors.delta }}>Delta: {(d.delta >= 0 ? "+" : "") + d.delta.toFixed(2)}</span>
                        <br />
                        <b style={{ color: "#2d2e82" }}>{d.band}</b>
                        <br />
                        <span style={{ fontSize: 14 }}>{d.band_desc}</span>
                      </div>
                    </div>
                  );
                }}
              />
              <Line
                type="monotone"
                dataKey="event_compound"
                stroke={chartColors.event}
                strokeWidth={2.5}
                name="Event Sentiment"
                dot={{ r: 3 }}
                isAnimationActive={false}
                activeDot={{
                  onClick: (_, index) => setActiveIndex(index)
                }}
              />
              <Line
                type="monotone"
                dataKey="feeling_compound"
                stroke={chartColors.feeling}
                strokeWidth={2.5}
                name="Feeling Sentiment"
                dot={{ r: 3 }}
                isAnimationActive={false}
                activeDot={{
                  onClick: (_, index) => setActiveIndex(index)
                }}
              />
              <Line
                type="linear"
                dataKey="delta"
                stroke={chartColors.delta}
                strokeDasharray="5 5"
                strokeWidth={2}
                name="Delta"
                dot={false}
                isAnimationActive={false}
              />
              {showBand && (
                <ReferenceDot
                  x={bandData[activeIndex].date}
                  y={bandData[activeIndex].event_compound}
                  r={7}
                  fill={chartColors.event}
                  stroke="#333"
                  strokeWidth={1}
                />
              )}
              {showBand && (
                <ReferenceDot
                  x={bandData[activeIndex].date}
                  y={bandData[activeIndex].feeling_compound}
                  r={7}
                  fill={chartColors.feeling}
                  stroke="#333"
                  strokeWidth={1}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
          {showBand && (
            <div className="band-label-tooltip" style={{
              background: "#f5faff",
              border: "1.5px solid #c7e0fb",
              borderRadius: 12,
              padding: 12,
              margin: "10px auto 0 auto",
              maxWidth: 450,
              fontSize: 15,
              boxShadow: "0 2px 9px #cbe3ff7c"
            }}>
              <b>{bandData[activeIndex].date}</b>
              <br />
              <span style={{ color: chartColors.event, fontWeight: 500 }}>
                Event: {bandData[activeIndex].event_sentiment} ({bandData[activeIndex].event_compound.toFixed(2)})
              </span>
              <br />
              <span style={{ color: chartColors.feeling, fontWeight: 500 }}>
                Feeling: {bandData[activeIndex].feeling_sentiment} ({bandData[activeIndex].feeling_compound.toFixed(2)})
              </span>
              <br />
              <span style={{ color: chartColors.delta, fontWeight: 500 }}>
                Delta: {(bandData[activeIndex].delta >= 0 ? "+" : "") + bandData[activeIndex].delta.toFixed(2)}
              </span>
              <br />
              <b style={{ color: "#2d2e82" }}>{bandData[activeIndex].band}</b>
              <br />
              <span style={{ fontSize: 14 }}>{bandData[activeIndex].band_desc}</span>
            </div>
          )}
          <div className="insights-caption" style={{ marginTop: 10 }}>
            <span>
              This chart reveals when your <b>story</b> and your <b>feelings</b> align—or diverge.
              Each point is color-banded by behavioral pattern.<br />
              Tap or scroll through points to see personalized explanations.
            </span>
          </div>
        </div>
        {/* 4. Heatmap Calendar: intensity by day (multi-year, stacked, blue, GH-style) */}
        <div className="insights-section">
          <h5>Daily Intensity Heatmap</h5>
          <div style={{ marginBottom: 18 }}>
            {years.length === 0 && (
              <div style={{ color: "#a7b6c7", fontStyle: "italic" }}>
                No observed intensities for calendar heatmap.
              </div>
            )}
            {years.map(year => {
              const yearEntries = calendarByYear[year];
              const { start, end } = getYearDomain(yearEntries);
              return (
                <div
                  key={year}
                  style={{
                    marginBottom: 30, // More vertical space between years
                    paddingBottom: 8,
                    overflowX: "auto",
                    maxWidth: "100%", // Responsive for modal
                  }}
                >
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#2b314a",
                      marginBottom: 4,
                      fontSize: 18,
                    }}
                  >
                    {year}
                  </div>
                  <div
                    style={{
                      width: 720, // wide enough for all months and labels
                      overflow: "visible", // prevent cutoff of labels
                    }}
                  >
                    <CalendarHeatmap
                      startDate={start}
                      endDate={end}
                      values={yearEntries}
                      classForValue={getHeatmapClass}
                      showWeekdayLabels={true}
                      showMonthLabels={true}
                      horizontal={true}
                      tooltipDataAttrs={value => ({
                        "data-tip":
                          value && value.date
                            ? `${value.date}: Intensity ${value.count}`
                            : "",
                      })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="heatmap-legend" style={{ marginBottom: 8 }}>
            <span
              style={{
                background: customCalendarTheme.colorEmpty,
                padding: "0 10px",
                borderRadius: 4,
                marginRight: 4,
                fontSize: 13,
              }}
            >
              Low
            </span>
            <span
              style={{
                background: customCalendarTheme.colorScale1,
                padding: "0 10px",
                borderRadius: 4,
                marginRight: 4,
                fontSize: 13,
              }}
            >
              +
            </span>
            <span
              style={{
                background: customCalendarTheme.colorScale2,
                padding: "0 10px",
                borderRadius: 4,
                marginRight: 4,
                fontSize: 13,
              }}
            >
              ++
            </span>
            <span
              style={{
                background: customCalendarTheme.colorScale3,
                padding: "0 10px",
                borderRadius: 4,
                marginRight: 4,
                fontSize: 13,
              }}
            >
              +++
            </span>
            <span
              style={{
                background: customCalendarTheme.colorScale4,
                padding: "0 10px",
                borderRadius: 4,
                fontSize: 13,
              }}
            >
              Peak
            </span>
          </div>
          <div className="insights-caption">
            Brighter blue squares indicate more emotionally intense days. Years are shown separately for clarity.
          </div>
        </div>
        {/* 5. Stats: intensity mean & IQR */}
        <div className="insights-section">
          <h5>Statistical Overview (Intensity)</h5>
          <div>
            <b>Average Intensity:</b> {formatNumber(intensity_mean, 2)}<br />
            <b>IQR (Interquartile Range):</b> {formatNumber(intensity_iqr[0])} – {formatNumber(intensity_iqr[1])}
          </div>
          <div className="insights-caption">
            The average gives your typical day. The IQR (middle 50%) shows what's "normal" for you—points above this range are outliers, and may deserve attention.
          </div>
        </div>
        {/* 6. Word frequency spider plot */}
        <div className="insights-section">
          <h5>Most Frequent Words (All Text Columns)</h5>
          <Tabs activeKey={wordTab} onSelect={k => setWordTab(k)} className="mb-2" justify>
            <Tab eventKey="bar" title="Bar Chart">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={radarData}>
                  <XAxis dataKey="word" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#60a5fa" />
                </BarChart>
              </ResponsiveContainer>
            </Tab>
            <Tab eventKey="spider" title="Spider Plot">
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart outerRadius={90} data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="word" />
                  <PolarRadiusAxis angle={30} domain={[0, Math.max(...radarData.map(d => d.count)) || 1]} />
                  <Radar name="Frequency" dataKey="count" stroke="#2563eb" fill="#3b82f6" fillOpacity={0.7} />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            </Tab>
          </Tabs>
          <div className="insights-caption">
            Your most-used words may reveal hidden patterns or concerns.
          </div>
        </div>
      </Modal.Body>
      {!historyMode && (
        <Modal.Footer>
          <Button variant="outline-success" onClick={onHide}>Close</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
export default InsightsTrends;
